import React, { useState, useEffect } from 'react';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { getGpsDevices, createGpsDevice, updateGpsDevice, deleteGpsDevice, GpsDevice } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface GpsDeviceManagementProps {
  skipLayout?: boolean;
}

const GpsDeviceManagement: React.FC<GpsDeviceManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [devices, setDevices] = useState<GpsDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingDevice, setEditingDevice] = useState<GpsDevice | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [formData, setFormData] = useState({
    imeiNo: '',
    traccarId: '',
    simNo: '', // Legacy support
    msisdnNo: '', // Legacy support
    simCards: [
      { provider: '', simNo: '', msisdnNo: '' },
      { provider: '', simNo: '', msisdnNo: '' },
    ],
    description: '',
    fuelEnabled: false,
    fuelSubDeviceName: '',
    fuelTypePorts: [{ type: '', port: '' }],
    fuelCalibration: [{ kvats: 0, litres: 0 }],
    fuelThreshold: 0,
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ imeiNo?: string; simNo?: string; simCards?: string; traccarId?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showFuelSettings, setShowFuelSettings] = useState(false);

  // Check URL hash for #dev to enable editing fuel settings
  useEffect(() => {
    const checkHash = () => {
      setShowFuelSettings(window.location.hash === '#dev');
    };

    // Check on mount
    checkHash();

    // Listen for hash changes
    window.addEventListener('hashchange', checkHash);

    return () => {
      window.removeEventListener('hashchange', checkHash);
    };
  }, []);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  useEffect(() => {
    fetchDevices(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Refetch when debounced search changes
  useEffect(() => {
    fetchDevices(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchDevices = async (page: number, size: number = pageSize) => {
    try {
      setLoading(true);
      const response = await getGpsDevices(page, size, undefined, debouncedSearchTerm || undefined);
      const data = response.data.data || [];
      const pagination = response.data.pagination;

      setDevices(data);
      if (pagination) {
        setCurrentPage(pagination.currentPage || page);
        setTotalItems(pagination.totalItems || data.length);
        setHasNextPage(!!pagination.hasNextPage);
        setHasPreviousPage(!!pagination.hasPreviousPage);
      } else {
        // Fallback if pagination is missing
        setCurrentPage(1);
        setTotalItems(data.length);
        setHasNextPage(false);
        setHasPreviousPage(false);
      }
    } catch (err: any) {
      setError('Failed to load GPS devices');
      console.error('Failed to load GPS devices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchDevices(currentPage, pageSize);
  };

  const handleOpenModal = (device?: GpsDevice, viewMode: boolean = false) => {
    setIsViewMode(viewMode);
    if (device) {
      setEditingDevice(device);
      // Populate simCards from device, or create empty array
      const deviceSimCards = device.spec.simCards && device.spec.simCards.length > 0
        ? device.spec.simCards
        : [];
      // Ensure we have at least 2 slots with required string properties
      const simCards = [
        {
          provider: deviceSimCards[0]?.provider || '',
          simNo: deviceSimCards[0]?.simNo || '',
          msisdnNo: deviceSimCards[0]?.msisdnNo || '',
        },
        {
          provider: deviceSimCards[1]?.provider || '',
          simNo: deviceSimCards[1]?.simNo || '',
          msisdnNo: deviceSimCards[1]?.msisdnNo || '',
        },
      ];

      setFormData({
        imeiNo: device.metadata.name || '',
        traccarId: device.spec.traccarId !== undefined && device.spec.traccarId !== null ? String(device.spec.traccarId) : '',
        simNo: device.spec.simNo || '', // Legacy support
        msisdnNo: device.spec.msisdnNo || '', // Legacy support
        simCards,
        description: device.spec.description || '',
        fuelEnabled: device.spec.fuelDevice?.enabled || false,
        fuelSubDeviceName: device.spec.fuelDevice?.subDeviceName || '',
        fuelTypePorts:
          device.spec.fuelDevice?.typePorts && device.spec.fuelDevice.typePorts.length > 0
            ? device.spec.fuelDevice.typePorts
            : [{ type: '', port: '' }],
        fuelCalibration:
          device.spec.fuelDevice?.calibration && device.spec.fuelDevice.calibration.length > 0
            ? device.spec.fuelDevice.calibration.map((cal: any) => ({
              kvats: cal.kvats !== undefined ? cal.kvats : (cal.kvatsReading !== undefined ? cal.kvatsReading : 0),
              litres: cal.litres || 0,
            }))
            : [{ kvats: 0, litres: 0 }],
        fuelThreshold: device.spec.fuelDevice?.threshold || 0,
      });
    } else {
      setEditingDevice(null);
      setFormData({
        imeiNo: '',
        traccarId: '',
        simNo: '',
        msisdnNo: '',
        simCards: [
          { provider: '', simNo: '', msisdnNo: '' },
          { provider: '', simNo: '', msisdnNo: '' },
        ],
        description: '',
        fuelEnabled: false,
        fuelSubDeviceName: '',
        fuelTypePorts: [{ type: '', port: '' }],
        fuelCalibration: [{ kvats: 0, litres: 0 }],
        fuelThreshold: 0,
      });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
  };

  // Helper function to check if fuel-related data exists in form
  const hasFuelData = () => {
    if (formData.fuelEnabled) return true;

    const hasTypePorts = formData.fuelTypePorts.some((tp) => tp.type.trim() && tp.port.trim());
    if (hasTypePorts) return true;

    const hasCalibration = formData.fuelCalibration.some((cal) => cal.kvats !== 0 || cal.litres !== 0);
    if (hasCalibration) return true;

    if (formData.fuelThreshold && formData.fuelThreshold > 0) return true;

    return false;
  };

  // Helper function to check if device has existing fuel data
  const hasExistingFuelData = (device: GpsDevice | null) => {
    if (!device || !device.spec.fuelDevice) return false;

    const fuelDevice = device.spec.fuelDevice;
    if (fuelDevice.enabled) return true;

    const hasTypePorts = fuelDevice.typePorts && fuelDevice.typePorts.length > 0;
    if (hasTypePorts) return true;

    const hasCalibration = fuelDevice.calibration && fuelDevice.calibration.length > 0;
    if (hasCalibration) return true;

    if (fuelDevice.threshold !== undefined && fuelDevice.threshold > 0) return true;

    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    if (!formData.imeiNo.trim()) {
      setValidationErrors({ imeiNo: 'IMEI number is required' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    // Validate at least one SIM number in simCards is required when fuel-related data is present
    const fuelDataExists = hasFuelData() || hasExistingFuelData(editingDevice);

    if (fuelDataExists) {
      // Only check simCards - simNo is no longer required
      const hasSimNumber = formData.simCards.some((card) => card.simNo?.trim());

      if (!hasSimNumber) {
        const errorMsg = 'At least one SIM number in SIM Cards is required when fuel device is enabled';
        setValidationErrors({ simCards: errorMsg });
        setError('Please fix the highlighted errors.');
        setSubmitting(false);
        return;
      }
    }

    let traccarIdPayload: number | '' | undefined = undefined;
    if (showFuelSettings) {
      const trimmedTraccarId = formData.traccarId.trim();
      if (trimmedTraccarId !== '') {
        const parsedTraccarId = Number(trimmedTraccarId);
        if (!Number.isInteger(parsedTraccarId) || parsedTraccarId < 0) {
          const errorMsg = 'Traccar ID must be a non-negative integer';
          setValidationErrors({ traccarId: errorMsg });
          setError('Please fix the highlighted errors.');
          setSubmitting(false);
          return;
        }
        traccarIdPayload = parsedTraccarId;
      } else {
        traccarIdPayload = '';
      }
    }

    try {
      // Process fuel device data
      const fuelDevice = {
        enabled: formData.fuelEnabled,
        subDeviceName: formData.fuelSubDeviceName.trim() || undefined,
        typePorts: formData.fuelTypePorts.filter((tp) => tp.type.trim() && tp.port.trim()),
        calibration: formData.fuelCalibration.filter((cal) => cal.kvats !== 0 || cal.litres !== 0),
        threshold: formData.fuelThreshold || undefined,
      };

      // Process simCards - filter out empty cards
      const simCards = formData.simCards.filter(
        (card) => card.provider.trim() || card.simNo.trim() || card.msisdnNo.trim()
      );

      // simNo is no longer required - only include it if explicitly provided (for backward compatibility)
      // simCards is the preferred method for SIM numbers
      const trimmedSimNo = formData.simNo.trim();
      const includeSimNo = trimmedSimNo ? trimmedSimNo : undefined; // Only include if has value

      if (editingDevice) {
        // Build update payload - include metadata.name (IMEI) and spec fields
        const updateData: any = {
          metadata: {
            name: formData.imeiNo.trim(),
          },
          spec: {
            msisdnNo: formData.msisdnNo.trim() || '', // Legacy support - empty string will clear msisdnNo on backend
            simCards: simCards.length > 0 ? simCards : undefined,
            description: formData.description.trim() || '',
            fuelDevice,
          },
        };

        // Only include simNo if explicitly provided (for backward compatibility with existing data)
        // If empty, send empty string to clear it (no restrictions)
        if (includeSimNo !== undefined) {
          updateData.spec.simNo = includeSimNo || '';
        }
        if (showFuelSettings) {
          updateData.spec.traccarId = traccarIdPayload;
        }

        await updateGpsDevice(editingDevice._id, updateData);
      } else {
        // For create, only include simNo if provided
        const data: any = {
          metadata: {
            name: formData.imeiNo.trim(),
          },
          spec: {
            msisdnNo: formData.msisdnNo.trim() || undefined, // Legacy support
            simCards: simCards.length > 0 ? simCards : undefined,
            description: formData.description.trim() || '',
            fuelDevice,
          },
        };

        // Only include simNo if provided
        if (includeSimNo) {
          data.spec.simNo = includeSimNo;
        }
        if (showFuelSettings) {
          data.spec.traccarId = traccarIdPayload === '' ? undefined : traccarIdPayload;
        }

        await createGpsDevice(data);
      }

      setShowModal(false);
      fetchDevices(currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    setDeviceToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deviceToDelete) return;

    try {
      await deleteGpsDevice(deviceToDelete.id);
      // After delete, refetch current page (or previous if current becomes empty)
      const targetPage = currentPage > 1 && devices.length === 1 ? currentPage - 1 : currentPage;
      fetchDevices(targetPage);
      setShowDeleteConfirm(false);
      setDeviceToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete GPS device. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setDeviceToDelete(null);
      setShowErrorModal(true);
    }
  };

  const content = (
    <>
      {/* <div className="page-header">
        <h2>GPS Device Management</h2>
        <p>Manage GPS tracking devices for ambulances</p>
      </div> */}

      <div className="card">
        <div className="card-header">
          {/* <h3 className="card-title">GPS Devices</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search by IMEI, SIM, MSISDN, or Traccar ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('gpsdevice.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add GPS Device
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading GPS devices...</div>
        ) : devices.length === 0 ? (
          <div className="empty-state">
            <h3>No GPS devices yet</h3>
            <p>Create your first GPS device to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 450px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>IMEI Number</th>
                    <th style={{ whiteSpace: 'nowrap' }}>SIM 1</th>
                    <th style={{ whiteSpace: 'nowrap' }}>SIM 2</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Description</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Traccar ID</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Fuel Device</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Ambulance</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Created</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => {
                    // Get SIM cards or fallback to legacy fields
                    const simCards = device.spec.simCards && device.spec.simCards.length > 0
                      ? device.spec.simCards
                      : (device.spec.simNo || device.spec.msisdnNo
                        ? [{ provider: undefined, simNo: device.spec.simNo, msisdnNo: device.spec.msisdnNo }]
                        : []);

                    const sim1 = simCards[0] || null;
                    const sim2 = simCards[1] || null;

                    const renderSimCell = (sim: any) => {
                      if (!sim || (!sim.provider && !sim.simNo && !sim.msisdnNo)) {
                        return <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>;
                      }
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '15px' }}>
                          {sim.provider && (
                            <div style={{ fontWeight: '500' }}>{sim.provider}</div>
                          )}
                          {sim.simNo && (
                            <div>{sim.simNo}</div>
                          )}
                          {sim.msisdnNo && (
                            <div>{sim.msisdnNo}</div>
                          )}
                        </div>
                      );
                    };

                    return (
                      <tr key={device._id}>
                        <td>
                          <strong>{device.metadata.name}</strong>
                        </td>
                        <td>{renderSimCell(sim1)}</td>
                        <td>{renderSimCell(sim2)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            const description = device.spec.description || '';
                            const displayText = description || 'No description';
                            const isLong = description.length > 25;
                            const truncatedText = isLong ? description.substring(0, 25) + '...' : description;

                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ color: description ? 'inherit' : '#999', fontStyle: description ? 'normal' : 'italic' }}>
                                  {isLong ? truncatedText : displayText}
                                </span>
                                {isLong && (
                                  <span
                                    title={description}
                                    style={{
                                      cursor: 'help',
                                      fontSize: '16px',
                                      color: '#667eea',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    ℹ️
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {device.spec.traccarId !== undefined && device.spec.traccarId !== null ? (
                            device.spec.traccarId
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {device.spec.fuelDevice?.enabled ? (
                            <span style={{ color: '#27ae60', fontWeight: '500' }}>ON</span>
                          ) : (
                            <span style={{ color: '#999' }}>OFF</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            if (!device.spec || !device.spec.ambulance) {
                              return <span style={{ color: '#aaa', fontStyle: 'italic' }}>—</span>;
                            }

                            const ambulanceName = device.spec.ambulance.name
                              ? device.spec.ambulance.name
                              : device.spec.ambulance.licensePlate
                                ? device.spec.ambulance.licensePlate
                                : (device.spec.ambulance.vehicleNumber || '—');

                            const isLong = ambulanceName.length > 25;
                            const truncatedText = isLong ? ambulanceName.substring(0, 25) + '...' : ambulanceName;

                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>{truncatedText}</span>
                                {isLong && (
                                  <span
                                    title={ambulanceName}
                                    style={{
                                      cursor: 'help',
                                      fontSize: '16px',
                                      color: '#667eea',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    ℹ️
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateDDMMYYYY(device.metadata.createdAt)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-icon btn-view"
                              onClick={() => handleOpenModal(device, true)}
                              style={{
                                backgroundColor: '#667eea',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '15px',
                                marginRight: '6px',
                              }}
                            >
                              View
                            </button>
                            {hasPermission('gpsdevice.update') && (
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleOpenModal(device, false)}
                              >
                                Edit
                              </button>
                            )}
                            {hasPermission('gpsdevice.delete') && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() =>
                                  handleDelete(
                                    device._id,
                                    device.metadata.name
                                  )
                                }
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      fetchDevices(1, newSize);
                    }}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100, 500].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                      currentPage * pageSize,
                      totalItems
                    )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && fetchDevices(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && fetchDevices(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {isViewMode ? 'View GPS Device' : editingDevice ? 'Edit GPS Device' : 'Create New GPS Device'}
              </h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>
                    IMEI Number
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.imeiNo}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, imeiNo: numericValue });
                    }}
                    className={validationErrors.imeiNo ? 'input-error' : ''}
                    placeholder="e.g., 1123421"
                    readOnly={isViewMode}
                    disabled={isViewMode}
                  />
                  {validationErrors.imeiNo && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.imeiNo}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px' }}>
                    International Mobile Equipment Identity number (required).
                  </small>
                </div>

                {/* SIM Cards - Side by Side */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                  {/* SIM Card 1 */}
                  <div style={{ padding: '0px', borderRadius: '0px', borderRight: isDark ? '1px solid #475569' : '1px solid #ddd', paddingRight: '20px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 16px 0' }}>
                      SIM 1
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Provider</label>
                        <select
                          value={formData.simCards[0].provider}
                          onChange={(e) => {
                            const newSimCards = [...formData.simCards];
                            newSimCards[0] = { ...newSimCards[0], provider: e.target.value };
                            setFormData({ ...formData, simCards: newSimCards });
                          }}
                          style={{ width: '100%', padding: '10px', border: isDark ? '1px solid #475569' : '1px solid #ddd', borderRadius: '6px' }}
                          disabled={isViewMode}
                        >
                          <option value="">Select Provider</option>
                          <option value="Jio">Jio</option>
                          <option value="BSNL">BSNL</option>
                          <option value="Airtel">Airtel</option>
                          <option value="VI">VI</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>SIM Number</label>
                        <input
                          type="text"
                          value={formData.simCards[0].simNo}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(/\D/g, '');
                            const newSimCards = [...formData.simCards];
                            newSimCards[0] = { ...newSimCards[0], simNo: numericValue };
                            setFormData({ ...formData, simCards: newSimCards });
                            if (validationErrors.simNo) {
                              setValidationErrors({ ...validationErrors, simNo: undefined });
                            }
                            if (validationErrors.simCards) {
                              setValidationErrors({ ...validationErrors, simCards: undefined });
                            }
                          }}
                          className={
                            validationErrors.simNo || validationErrors.simCards ? 'input-error' : ''
                          }
                          style={
                            validationErrors.simNo || validationErrors.simCards
                              ? { 
                                  borderColor: '#e74c3c', 
                                  borderWidth: '2px', 
                                  borderStyle: 'solid',
                                  boxShadow: '0 0 0 1px #e74c3c'
                                }
                              : undefined
                          }
                          placeholder="e.g., 12312313"
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>MSISDN Number</label>
                        <input
                          type="text"
                          value={formData.simCards[0].msisdnNo}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(/\D/g, '');
                            const newSimCards = [...formData.simCards];
                            newSimCards[0] = { ...newSimCards[0], msisdnNo: numericValue };
                            setFormData({ ...formData, simCards: newSimCards });
                          }}
                          placeholder="e.g., 2347824"
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                    {validationErrors.simNo && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '8px', display: 'block' }}>
                        {validationErrors.simNo}
                      </small>
                    )}
                  </div>

                  {/* SIM Card 2 */}
                  <div style={{ padding: '0px', borderRadius: '0px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 16px 0' }}>SIM 2</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Provider</label>
                        <select
                          value={formData.simCards[1].provider}
                          onChange={(e) => {
                            const newSimCards = [...formData.simCards];
                            newSimCards[1] = { ...newSimCards[1], provider: e.target.value };
                            setFormData({ ...formData, simCards: newSimCards });
                          }}
                          style={{ width: '100%', padding: '10px', border: isDark ? '1px solid #475569' : '1px solid #ddd', borderRadius: '6px' }}
                          disabled={isViewMode}
                        >
                          <option value="">Select Provider</option>
                          <option value="Jio">Jio</option>
                          <option value="BSNL">BSNL</option>
                          <option value="Airtel">Airtel</option>
                          <option value="VI">VI</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>SIM Number</label>
                        <input
                          type="text"
                          value={formData.simCards[1].simNo}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(/\D/g, '');
                            const newSimCards = [...formData.simCards];
                            newSimCards[1] = { ...newSimCards[1], simNo: numericValue };
                            setFormData({ ...formData, simCards: newSimCards });
                            if (validationErrors.simNo) {
                              setValidationErrors({ ...validationErrors, simNo: undefined });
                            }
                            if (validationErrors.simCards) {
                              setValidationErrors({ ...validationErrors, simCards: undefined });
                            }
                          }}
                          className={
                            validationErrors.simNo || validationErrors.simCards ? 'input-error' : ''
                          }
                          style={
                            validationErrors.simNo || validationErrors.simCards
                              ? { 
                                  borderColor: '#e74c3c', 
                                  borderWidth: '2px', 
                                  borderStyle: 'solid',
                                  boxShadow: '0 0 0 1px #e74c3c'
                                }
                              : undefined
                          }
                          placeholder="e.g., 12312313"
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>MSISDN Number</label>
                        <input
                          type="text"
                          value={formData.simCards[1].msisdnNo}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(/\D/g, '');
                            const newSimCards = [...formData.simCards];
                            newSimCards[1] = { ...newSimCards[1], msisdnNo: numericValue };
                            setFormData({ ...formData, simCards: newSimCards });
                          }}
                          placeholder="e.g., 2347824"
                          readOnly={isViewMode}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {validationErrors.simCards && (
                  <small style={{ 
                    color: '#e74c3c', 
                    fontSize: '14px', 
                    marginTop: '8px', 
                    marginBottom: '16px', 
                    display: 'block',
                    fontWeight: '600',
                    backgroundColor: 'rgba(231, 76, 60, 0.1)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #e74c3c'
                  }}>
                    {validationErrors.simCards}
                  </small>
                )}

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter a description for this device (optional)"
                    rows={4}
                    readOnly={isViewMode}
                    disabled={isViewMode}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>

                {/* Fuel Device Configuration - Always visible, editable only when #dev is in URL */}
                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Fuel Device Configuration</h4>
                  </div>
                  {!showFuelSettings && (
                    <div style={{
                      padding: '12px',
                      marginBottom: '16px',
                      backgroundColor: isDark ? '#1e293b' : '#fff3cd',
                      border: isDark ? '1px solid #475569' : '1px solid #ffc107',
                      borderRadius: '6px',
                      color: isDark ? '#cbd5e1' : '#856404',
                      fontSize: '16px',
                    }}>
                      ⚠️ Fuel configuration is disabled.
                    </div>
                  )}

                  {showFuelSettings && (
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={formData.fuelEnabled}
                          onChange={(e) => setFormData({ ...formData, fuelEnabled: e.target.checked })}
                          disabled={isViewMode}
                          style={{ width: 'auto', cursor: !isViewMode ? 'pointer' : 'not-allowed', opacity: !isViewMode ? 1 : 0.6 }}
                        />
                        <span>Fuel Device Enabled</span>
                      </label>
                    </div>
                  )}

                  {formData.fuelEnabled && (
                    <>
                      <div className="form-group">
                        <label>
                          Traccar ID
                        </label>
                        <input
                          type="text"
                          value={formData.traccarId}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(/\D/g, '');
                            setFormData({ ...formData, traccarId: numericValue });
                            if (validationErrors.traccarId) {
                              setValidationErrors({ ...validationErrors, traccarId: undefined });
                            }
                          }}
                          className={validationErrors.traccarId ? 'input-error' : ''}
                          placeholder="e.g., 12345"
                          readOnly={isViewMode || !showFuelSettings}
                          disabled={isViewMode || !showFuelSettings}
                        />
                        {validationErrors.traccarId && (
                          <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            {validationErrors.traccarId}
                          </small>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Sub Device Name</label>
                        <input
                          type="text"
                          value={formData.fuelSubDeviceName}
                          onChange={(e) => {
                            if (!showFuelSettings || isViewMode) return;
                            setFormData({ ...formData, fuelSubDeviceName: e.target.value });
                          }}
                          disabled={!showFuelSettings || isViewMode}
                          readOnly={isViewMode}
                          placeholder="e.g., FuelRod1, FuelSensor1"
                          style={{
                            padding: '8px',
                            borderRadius: '4px',
                            cursor: showFuelSettings ? 'text' : 'not-allowed',
                            opacity: showFuelSettings ? 1 : 0.6,
                            backgroundColor: showFuelSettings ? 'white' : '#f5f5f5'
                          }}
                        />
                        <small style={{ color: '#666', fontSize: '14px' }}>
                          Name for the fuel sub-device (optional).
                        </small>
                      </div>

                      <div className="form-group">
                        <label>Threshold (Percentage)</label>
                        <input
                          type="text"
                          value={formData.fuelThreshold}
                          onChange={(e) => {
                            if (!showFuelSettings || isViewMode) return;
                            const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                            if (numericValue === '' || numericValue === '.') {
                              setFormData({ ...formData, fuelThreshold: 0 });
                              return;
                            }
                            const numValue = parseFloat(numericValue);
                            if (!isNaN(numValue)) {
                              // Clamp value between 0 and 100
                              const clampedValue = Math.max(0, Math.min(100, numValue));
                              setFormData({ ...formData, fuelThreshold: clampedValue });
                            }
                          }}
                          disabled={!showFuelSettings || isViewMode}
                          readOnly={isViewMode}
                          placeholder="e.g., 10, 25.5"
                          style={{
                            padding: '8px',
                            borderRadius: '4px',
                            cursor: showFuelSettings ? 'text' : 'not-allowed',
                            opacity: showFuelSettings ? 1 : 0.6,
                            backgroundColor: showFuelSettings ? 'white' : '#f5f5f5'
                          }}
                        />
                        <small style={{ color: '#666', fontSize: '14px' }}>
                          Fuel threshold percentage (0-100).
                        </small>
                      </div>

                      <div className="form-group">
                        <label>Type and Port (Multiple entries)</label>
                        {formData.fuelTypePorts.map((tp, index) => (
                          <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <input
                              type="text"
                              value={tp.type}
                              onChange={(e) => {
                                if (!showFuelSettings) return;
                                const alphanumericValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                const newTypePorts = [...formData.fuelTypePorts];
                                newTypePorts[index].type = alphanumericValue;
                                setFormData({ ...formData, fuelTypePorts: newTypePorts });
                              }}
                              disabled={!showFuelSettings || isViewMode}
                              readOnly={isViewMode}
                              placeholder="Type (e.g., Humidity, Fuel)"
                              style={{
                                flex: 1,
                                cursor: showFuelSettings ? 'text' : 'not-allowed',
                                opacity: showFuelSettings ? 1 : 0.6,
                                backgroundColor: showFuelSettings ? 'white' : '#f5f5f5'
                              }}
                            />
                            <input
                              type="text"
                              value={tp.port}
                              onChange={(e) => {
                                if (!showFuelSettings) return;
                                const alphanumericValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                                const newTypePorts = [...formData.fuelTypePorts];
                                newTypePorts[index].port = alphanumericValue;
                                setFormData({ ...formData, fuelTypePorts: newTypePorts });
                              }}
                              disabled={!showFuelSettings || isViewMode}
                              readOnly={isViewMode}
                              placeholder="Port (e.g., io206, io305)"
                              style={{
                                flex: 1,
                                cursor: showFuelSettings ? 'text' : 'not-allowed',
                                opacity: showFuelSettings ? 1 : 0.6,
                                backgroundColor: showFuelSettings ? 'white' : '#f5f5f5'
                              }}
                            />
                            {formData.fuelTypePorts.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!showFuelSettings) return;
                                  const newTypePorts = formData.fuelTypePorts.filter((_, i) => i !== index);
                                  setFormData({ ...formData, fuelTypePorts: newTypePorts });
                                }}
                                disabled={!showFuelSettings || isViewMode}
                                style={{
                                  padding: '8px 12px',
                                  background: showFuelSettings ? '#e74c3c' : '#ccc',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: showFuelSettings ? 'pointer' : 'not-allowed',
                                  opacity: showFuelSettings ? 1 : 0.6,
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        {showFuelSettings && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                fuelTypePorts: [...formData.fuelTypePorts, { type: '', port: '' }],
                              });
                            }}
                            style={{
                              marginTop: '8px',
                              padding: '8px 16px',
                              background: '#3498db',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            + Add Type/Port
                          </button>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Fuel Calibration (Litres vs Kvats)</label>
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#666' }}>
                            <div>Litres</div>
                            <div>Kvats</div>
                            <div></div>
                          </div>
                          {formData.fuelCalibration.map((cal, index) => (
                            <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', marginBottom: '8px' }}>
                              <input
                                type="text"
                                value={cal.litres || ''}
                                onChange={(e) => {
                                  if (!showFuelSettings) return;
                                  const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                                  const newCalibration = [...formData.fuelCalibration];
                                  newCalibration[index].litres = numericValue ? parseFloat(numericValue) : 0;
                                  setFormData({ ...formData, fuelCalibration: newCalibration });
                                }}
                                disabled={!showFuelSettings || isViewMode}
                                readOnly={isViewMode}
                                placeholder="e.g., 0, 25"
                                style={{
                                  padding: '8px',
                                  borderRadius: '4px',
                                  cursor: showFuelSettings ? 'text' : 'not-allowed',
                                  opacity: showFuelSettings ? 1 : 0.6,
                                  backgroundColor: showFuelSettings ? 'white' : '#f5f5f5'
                                }}
                              />
                              <input
                                type="text"
                                value={cal.kvats || ''}
                                onChange={(e) => {
                                  if (!showFuelSettings) return;
                                  const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                                  const newCalibration = [...formData.fuelCalibration];
                                  newCalibration[index].kvats = numericValue ? parseFloat(numericValue) : 0;
                                  setFormData({ ...formData, fuelCalibration: newCalibration });
                                }}
                                disabled={!showFuelSettings || isViewMode}
                                readOnly={isViewMode}
                                placeholder="e.g., 30, 4000"
                                style={{
                                  padding: '8px',
                                  borderRadius: '4px',
                                  cursor: showFuelSettings ? 'text' : 'not-allowed',
                                  opacity: showFuelSettings ? 1 : 0.6,
                                  backgroundColor: showFuelSettings ? 'white' : '#f5f5f5'
                                }}
                              />
                              {formData.fuelCalibration.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!showFuelSettings) return;
                                    const newCalibration = formData.fuelCalibration.filter((_, i) => i !== index);
                                    setFormData({ ...formData, fuelCalibration: newCalibration });
                                  }}
                                  disabled={!showFuelSettings || isViewMode}
                                  style={{
                                    padding: '8px 12px',
                                    background: showFuelSettings ? '#e74c3c' : '#ccc',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: showFuelSettings ? 'pointer' : 'not-allowed',
                                    opacity: showFuelSettings ? 1 : 0.6,
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {showFuelSettings && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                fuelCalibration: [...formData.fuelCalibration, { kvats: 0, litres: 0 }],
                              });
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#3498db',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            + Add Calibration Entry
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {!isViewMode && (
                <div className="drawer-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <span className="loading-spinner btn-spinner" aria-hidden />
                        {editingDevice ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      `${editingDevice ? 'Update' : 'Create'} Device`
                    )}
                  </button>
                </div>
              )}
              {isViewMode && (
                <div className="drawer-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                    Close
                  </button>
                  {hasPermission('gpsdevice.update') && (
                    <button
                      type="button"
                      className="btn-submit"
                      onClick={() => setIsViewMode(false)}
                      style={{ marginLeft: '8px' }}
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete GPS Device"
        message={`Are you sure you want to delete the GPS device "${deviceToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeviceToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default GpsDeviceManagement;


